import { useEffect, useRef } from "react";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #f9f9f7;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }

  /* ── FULL-PAGE WRAPPER — same as every section ── */
  #page-nf {
    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 36px;
    padding: 100px 80px;
    max-width: 1300px;
    margin: 0 auto;
    overflow: hidden;
  }

  /* ── MAIN CARD — same as .ab-intro-card ── */
  .pge-card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 560px;
    background: #fff;
    border: 1.5px solid #e8e8e4;
    border-radius: 24px;
    padding: 52px 48px 44px;
    text-align: center;
    box-shadow: 0 8px 48px rgba(0,0,0,0.06);
    opacity: 0;
    animation: pge-up 0.65s ease 0.08s forwards;
  }


  /* stagger children */
  .pge-card > * {
    opacity: 0;
    animation: pge-up 0.55s ease forwards;
  }
  .pge-card > :nth-child(1) { animation-delay: 0.16s; }
  .pge-card > :nth-child(2) { animation-delay: 0.22s; }
  .pge-card > :nth-child(3) { animation-delay: 0.28s; }
  .pge-card > :nth-child(4) { animation-delay: 0.34s; }
  .pge-card > :nth-child(5) { animation-delay: 0.40s; }
  .pge-card > :nth-child(6) { animation-delay: 0.45s; }
  .pge-card > :nth-child(7) { animation-delay: 0.50s; }

  /* ── ICON BADGE ── */
  .pge-icon-wrap {
    width: 68px; height: 68px;
    border-radius: 20px;
    background: rgba(245,166,35,0.09);
    border: 1.5px solid rgba(245,166,35,0.22);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 24px;
    color: #f5a623;
  }

  /* ── EYEBROW TAG — same as skills/about section tags ── */
  .pge-tag {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 14px;
    background: rgba(245,166,35,0.10);
    border: 1px solid rgba(245,166,35,0.22);
    border-radius: 50px;
    font-size: 0.68rem;
    font-weight: 700;
    color: #f5a623;
    letter-spacing: 0.10em;
    text-transform: uppercase;
    margin-bottom: 24px;
  }
  .pge-tag-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #f5a623;
    animation: pge-pulse 2s ease-in-out infinite;
  }
  @keyframes pge-pulse {
    0%,100% { box-shadow: 0 0 0 0   rgba(245,166,35,0.5); }
    50%      { box-shadow: 0 0 0 4px rgba(245,166,35,0);   }
  }

  /* ── 404 — Georgia serif same as h-name ── */
  .pge-code {
    font-family: Georgia, serif;
    font-size: clamp(5rem, 16vw, 8rem);
    font-weight: 700;
    color: #1e1e1e;
    letter-spacing: -5px;
    line-height: 1;
    margin-bottom: 4px;
  }
  .pge-code em {
    color: #f5a623;
    font-style: normal;
  }

  /* gold underline — same as se-underline / ab-underline */
  .pge-underline {
    width: 58px; height: 4px;
    background: #f5a623;
    border-radius: 4px;
    margin: 14px auto 26px;
    transform: scaleX(0);
    transform-origin: center;
    animation: pge-bar 0.5s ease 0.72s forwards;
  }
  @keyframes pge-bar { to { transform: scaleX(1); } }

  /* ── HEADING — Georgia serif, same as h-name / se-header h2 ── */
  .pge-heading {
    font-family: Georgia, serif;
    font-size: clamp(1.25rem, 3vw, 1.72rem);
    font-weight: 700;
    color: #1e1e1e;
    letter-spacing: -0.3px;
    line-height: 1.25;
    margin-bottom: 14px;
  }

  /* ── DESCRIPTION — same as h-desc ── */
  .pge-desc {
    font-size: 0.92rem;
    color: #777;
    line-height: 1.82;
    max-width: 390px;
    margin: 0 auto 26px;
  }
  .pge-desc strong { color: #444; font-weight: 600; }

  /* ── URL BADGE — shows the bad URL ── */
  .pge-url-badge {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 9px 16px;
    background: #f9f9f7;
    border: 1.5px solid #e8e8e4;
    border-radius: 12px;
    margin-bottom: 28px;
    max-width: 100%;
    overflow: hidden;
  }
  .pge-url-label {
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.10em;
    text-transform: uppercase;
    color: #bbb;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .pge-url-sep { width: 1px; height: 14px; background: #e8e8e4; flex-shrink: 0; }
  .pge-url-value {
    font-family: 'Courier New', monospace;
    font-size: 0.78rem;
    color: #f5a623;
    font-weight: 600;
    background: rgba(245,166,35,0.08);
    padding: 2px 9px;
    border-radius: 6px;
    border: 1px solid rgba(245,166,35,0.2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 240px;
  }

  /* divider */
  .pge-divider {
    height: 1px;
    background: #eeeeea;
    margin: 0 0 28px;
  }

  /* ── CTA BUTTONS — same as h-btn-dark / h-btn-outline in hero ── */
  .pge-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }

  /* primary dark→gold — same as .h-btn-dark */
  .pge-btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 22px; border-radius: 12px;
    background: #1e1e1e; color: #fff;
    font-family: inherit; font-size: 0.875rem; font-weight: 700;
    border: none; cursor: pointer; text-decoration: none;
    transition: all 0.28s ease;
    position: relative; overflow: hidden;
  }
  .pge-btn-primary::before {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(135deg, #f5a623, #e8920f);
    opacity: 0; transition: opacity 0.28s;
  }
  .pge-btn-primary:hover::before { opacity: 1; }
  .pge-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(245,166,35,0.30);
  }
  .pge-btn-primary span,
  .pge-btn-primary svg { position: relative; z-index: 1; }

  /* outline — same as .h-btn-outline */
  .pge-btn-outline {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 22px; border-radius: 12px;
    background: #fff; color: #1e1e1e;
    font-family: inherit; font-size: 0.875rem; font-weight: 600;
    border: 1.5px solid #e8e8e4; cursor: pointer; text-decoration: none;
    transition: all 0.25s ease;
  }
  .pge-btn-outline:hover {
    border-color: rgba(245,166,35,0.45);
    color: #f5a623;
    background: #fffbf2;
    transform: translateY(-2px);
  }

  /* ── QUICK LINKS — same pill style as se-filter-btn ── */
  .pge-links {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    opacity: 0;
    animation: pge-up 0.6s ease 0.62s forwards;
  }
  .pge-links-label {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #c8c4bc;
  }
  .pge-links-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .pge-link-pill {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 7px 16px;
    border-radius: 50px;
    border: 1.5px solid #e8e8e4;
    background: #fff;
    font-family: inherit;
    font-size: 0.78rem; font-weight: 500;
    color: #555;
    text-decoration: none;
    transition: all 0.22s ease;
    box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  }
  .pge-link-pill:hover {
    border-color: rgba(245,166,35,0.40);
    color: #f5a623;
    background: #fffbf2;
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(245,166,35,0.11);
  }

  /* shared keyframe — same name pattern as every section (h-up, ab-up, etc.) */
  @keyframes pge-up {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 900px) {
    #page-nf { padding: 80px 32px; }
  }
  @media (max-width: 768px) {
    #page-nf  { padding: 70px 20px; }
    .pge-card { padding: 40px 26px 36px; }
  }
  @media (max-width: 560px) {
    #page-nf       { padding: 56px 16px; gap: 28px; }
    .pge-card      { padding: 34px 18px 28px; }
    .pge-code      { letter-spacing: -3px; }
    .pge-actions   { flex-direction: column; align-items: stretch; }
    .pge-btn-primary,
    .pge-btn-outline { justify-content: center; }
    .pge-url-value { max-width: 160px; }
    .pge-blob-tl   { width: 200px; height: 200px; }
    .pge-blob-br   { width: 180px; height: 180px; }
  }
`;

// ─── ICONS ────────────────────────────────────────────────────────────────────
const HomeIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);
const MapPinIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="10" r="3" />
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <line
      x1="8"
      y1="22"
      x2="16"
      y2="22"
      strokeWidth="1.5"
      strokeOpacity="0.35"
    />
  </svg>
);

export default function PageNotFound({
  badUrl = typeof window !== "undefined"
    ? window.location.pathname
    : "/unknown",
  onGoBack = () => window.history.back(),
  onGoHome = "#home",
}) {
  return (
    <>
      <style>{CSS}</style>

      <section id="page-nf">
        <div className="pge-card">
          <div className="pge-icon-wrap">
            <MapPinIcon />
          </div>

          {/* eyebrow tag */}
          <div className="pge-tag">
            <span className="pge-tag-dot" />
            Page Not Found
          </div>

          <div className="pge-code">
            4<em>0</em>4
          </div>

          <div className="pge-underline" />

          {/* heading */}
          <h1 className="pge-heading">This Page Doesn't Exist</h1>

          {/* description */}
          <p className="pge-desc">
            The URL you entered <strong>doesn't match any page</strong> on this
            portfolio. It may have been moved, deleted, or you may have mistyped
            the address.
          </p>

          {/* divider */}
          <div className="pge-divider" />

          {/* action buttons */}
          <div className="pge-actions">
            <button className="pge-btn-primary" onClick={onGoBack}>
              <ArrowLeftIcon />
              <span>Go Back</span>
            </button>
            <a href={onGoHome} className="pge-btn-outline">
              <HomeIcon />
              Back to Home
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
